import React from "react";

import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { CONFIGURATION_SECTION_PATH } from "../../../components/layout/adminMenu";
import { Type } from "../../../models/configuration/definitions/widget";
import { frontEditState } from "../atoms/frontEdit";
import { getCurrentContentEditsSelector } from "../selectors/currentContentEdits";
import useFrontEditModal from "./useFrontEditModal";

type UseFrontEditEditable = {
  widgetConfiguration: Object;
  FrontEditWidgetWrapper: any;
};

const PREFIX = "WidgetWrapper";

const classes = {
  button: `${PREFIX}-button`,
  label: `${PREFIX}-label`,
  innerButton: `${PREFIX}-innerButton`,
};

const StyledWidgetWrapper = styled(Box)(() => ({
  width: "100%",
  display: "flex",

  [`& .${classes.button}`]: {
    borderRadius: 0,
    border: "none",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",

    [`& .${classes.innerButton}`]: {
      border: "1px solid lightblue",
      display: "flex",
      flex: 1,
      pointerEvents: "none",
    },

    [`& .${classes.label}`]: {
      width: "fit-content",
      background: "lightblue",
      display: "block",
      position: "relative",
      color: "#116ddf",
      fontSize: "12px",
      padding: "5px",
    },

    ":hover": {
      [`& .${classes.innerButton}`]: {
        border: "1px solid #116ddf",
      },

      [`& .${classes.label}`]: {
        background: "#116ddf",
        color: "white",
      },
    },
  },
}));

const getWidgetConfigSectionName = (
  widgetType: Type | undefined,
): CONFIGURATION_SECTION_PATH | undefined => {
  switch (widgetType) {
    case "edito":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_EDITO_WIDGET;
    case "mosaic":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_MOSAIC;
    case "footer":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_FOOTER_WIDGET;
    case "image":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_K_IMAGE_WIDGET;
    case "video":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_K_VIDEO_WIDGET;
    case "header":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_K_HEADER_WIDGET;
    case "search":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_SEARCH_WIDGET;
    case "news":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_NEWS_WIDGET;
    case "sectionTitle":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_SECTION_TITLE_WIDGET;
    case "menu":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_MENU_WIDGET;
    case "report":
      return CONFIGURATION_SECTION_PATH.COMPONENTS_REPORT_WIDGET;
    default:
      return undefined;
  }
};

// This hook is used in KWidgets to wrap front editable widgets
export default function useFrontEditEditable(
  type: Type | undefined,
  widgetId: string,
): UseFrontEditEditable {
  const { t } = useTranslation();
  const { setIsOpen, setCurrentEditKeys } = useFrontEditModal();
  const configurationPath = getWidgetConfigSectionName(type);
  const isFrontEdit = useRecoilValue(frontEditState);

  const endpoint = () => {
    if (!isFrontEdit) {
      return undefined;
    }
    if (configurationPath) {
      return `${configurationPath}/${widgetId}`;
    }
  };

  const currentContentEdit = useRecoilValue(
    getCurrentContentEditsSelector({
      endpoint: endpoint(),
    }),
  );

  const widgetConfiguration = currentContentEdit?.data || undefined;

  const FrontEditWidgetWrapper = React.useMemo(() => {
    const WidgetWrapper: React.FC<React.PropsWithChildren<unknown>> = function ({ children }) {
      return (
        <StyledWidgetWrapper>
          <Box
            style={{ width: "100%", textAlign: "initial" }}
            className={classes.button}
            onClick={() => {
              setCurrentEditKeys([{ widgetId, configSection: configurationPath || "" }]);
              setIsOpen(true);
            }}
          >
            <Typography className={classes.label}>
              {t("front-edit.widget.label", {
                widgetType: type && type[0].toUpperCase() + type.substring(1),
              })}
            </Typography>
            <Box className={classes.innerButton}>{children}</Box>
          </Box>
        </StyledWidgetWrapper>
      );
    };
    return WidgetWrapper;
  }, [configurationPath, setCurrentEditKeys, setIsOpen, t, type, widgetId]);

  return {
    widgetConfiguration,
    FrontEditWidgetWrapper,
  };
}
